/**
 * Trunnion AI - Chat SSE Streaming Hook
 * Subscribes to Server-Sent Events for streaming agent chat responses.
 */

import { useCallback, useRef } from 'react';
import { chatApi } from '@/api/chat.api';
import { useChatStore } from '@/stores/chatStore';
import type { ChatStreamEvent, ChatMessage } from '@/types/chat.types';

export function useChatStream(agentId: string) {
  const abortRef = useRef<AbortController | null>(null);
  const {
    activeSessionId,
    setStreaming,
    appendStreamingContent,
    resetStreamingContent,
    addStreamingToolCall,
    updateStreamingToolCall,
    addStreamingReasoning,
    addMessage,
  } = useChatStore();

  const sendMessage = useCallback(
    async (message: string, classificationLevel = 'unclassified') => {
      // Cancel any existing stream
      abortRef.current?.abort();
      abortRef.current = new AbortController();

      resetStreamingContent();
      setStreaming(true);

      // Add user message immediately
      const userMessage: ChatMessage = {
        id: `temp-${Date.now()}`,
        sessionId: activeSessionId ?? '',
        role: 'user',
        content: message,
        classificationLevel,
        createdAt: new Date().toISOString(),
      };
      addMessage(userMessage);

      try {
        const response = await chatApi.sendMessage(
          agentId,
          message,
          activeSessionId,
          classificationLevel,
        );

        if (!response.body) {
          throw new Error('No response body for streaming');
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() ?? '';

          for (const line of lines) {
            if (!line.startsWith('data: ')) continue;
            const jsonStr = line.slice(6).trim();
            if (!jsonStr || jsonStr === '[DONE]') continue;

            try {
              const event: ChatStreamEvent = JSON.parse(jsonStr);

              switch (event.type) {
                case 'token':
                  if (event.content) appendStreamingContent(event.content);
                  break;
                case 'tool_call':
                  if (event.toolCall) {
                    if (event.toolCall.status === 'pending' || event.toolCall.status === 'running') {
                      addStreamingToolCall(event.toolCall);
                    } else {
                      updateStreamingToolCall(event.toolCall.id, event.toolCall);
                    }
                  }
                  break;
                case 'reasoning':
                  if (event.reasoning) addStreamingReasoning(event.reasoning);
                  break;
                case 'done':
                  if (event.message) addMessage(event.message);
                  break;
                case 'error':
                  console.error('Chat stream error:', event.error);
                  break;
              }
            } catch {
              // Skip malformed JSON
            }
          }
        }
      } catch (err) {
        if ((err as Error).name !== 'AbortError') {
          console.error('Chat stream failed:', err);
        }
      } finally {
        setStreaming(false);
        resetStreamingContent();
      }
    },
    [agentId, activeSessionId, setStreaming, appendStreamingContent, resetStreamingContent, addStreamingToolCall, updateStreamingToolCall, addStreamingReasoning, addMessage],
  );

  const cancelStream = useCallback(() => {
    abortRef.current?.abort();
    setStreaming(false);
  }, [setStreaming]);

  return { sendMessage, cancelStream };
}
